if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

connectedCallback() {
  this.onVariantChangeUnsubscriber = subscribe(
    PUB_SUB_EVENTS.optionValueSelectionChange,
    this.handleOptionValueChange.bind(this)
  );

  this.initQuantityHandlers();
  this.filterImagesOnLoad();

  // Direct fallback listener for variant changes
  const variantSelects = this.querySelector('variant-selects');
  if (variantSelects) {
    variantSelects.addEventListener('change', () => {
      setTimeout(() => {
        const options = Array.from(
          variantSelects.querySelectorAll('input[type="radio"]:checked')
        ).map((r) => r.value);

        const variant = window.ShopifyAnalytics?.meta?.product?.variants?.find(
          (v) => v.public_title === options.join(' / ')
        );

        if (variant) {
          const selectedVariantEl = variantSelects.querySelector('[data-selected-variant]');
          if (selectedVariantEl) selectedVariantEl.innerHTML = JSON.stringify(variant);

          this.updateURL(this.dataset.url, variant.id);
          this.updateVariantInputs(variant.id);
          this.updateLocalProductInfo(variant);
          this.updateMedia(null, variant.featured_media?.id, variant);
        } else {
          this.setUnavailable();
        }
      }, 50);
    });
  }
}

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
      }

      // =========================
      // ✅ VARIANT CHANGE
      // =========================
      handleOptionValueChange({ data: { event } }) {
        if (!this.contains(event.target)) return;

        const variant = this.getSelectedVariant(this);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateURL(this.dataset.url, variant.id);
        this.updateVariantInputs(variant.id);
        this.updateLocalProductInfo(variant);
        this.updateMedia(null, variant.featured_media?.id, variant);
      }

      // =========================
      // ✅ UI UPDATES
      // =========================
      updateLocalProductInfo(variant) {
        // No price is written on variant change: the price slot carries a contact
        // message instead (plan 0338). This used to overwrite #price-* with a bare
        // `$` + shop-currency amount, which both put the price back after a variant
        // click and ignored Markets.

        const skuEl = this.querySelector('[id^="Sku-"]');
        if (skuEl) {
          skuEl.textContent = variant.sku || '';
        }

        const inventoryEl = this.querySelector('[id^="Inventory-"]');
        if (inventoryEl) {
          inventoryEl.textContent = variant.available ? 'In stock' : 'Out of stock';
        }

        this.productForm?.toggleSubmitButton(
          !variant.available,
          variant.available ? '' : window.variantStrings.soldOut
        );
      }

      // =========================
      // ✅ VARIANT HELPERS
      // =========================
      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          'variant-selects [data-selected-variant]'
        )?.innerHTML;

        return selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}?variant=${variantId}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
      }

      // =========================
      // ✅ IMAGE FILTERING
      // =========================
      filterImagesOnLoad() {
        const variant = this.getSelectedVariant(this);
        if (!variant) return;

        setTimeout(() => {
          this.updateMedia(null, variant.featured_media?.id, variant);
        }, 200);
      }

      updateMedia(_, __, variant) {
        if (!variant) return;

        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return;

        const normalize = (str) =>
          (str || '')
            .toLowerCase()
            .trim()
            .replace(/[-_]/g, ' ')
            .replace(/\s+/g, ' ');

        let selectedColor = null;

        const groups = Array.from(
          variantSelects.querySelectorAll(
            'fieldset, .product-form__input, .variant-input'
          )
        );

        for (const g of groups) {
          const labelText =
            g.querySelector('legend')?.textContent ||
            g.querySelector('.form__label')?.textContent ||
            '';

          if (normalize(labelText).includes('color')) {
            const checked = g.querySelector('input[type="radio"]:checked');
            if (checked?.value) {
              selectedColor = normalize(checked.value);
              break;
            }

            const select = g.querySelector('select');
            if (select) {
              const opt = select.options[select.selectedIndex];
              selectedColor = normalize(opt?.value || opt?.textContent);
              break;
            }
          }
        }

        if (!selectedColor) return;

        const similarity = (s1, s2) => {
          const longer = s1.length > s2.length ? s1 : s2;
          const shorter = s1.length > s2.length ? s2 : s1;

          if (longer.length === 0) return 1;

          const editDistance = (a, b) => {
            const matrix = [];
            for (let i = 0; i <= b.length; i++) matrix[i] = [i];
            for (let j = 0; j <= a.length; j++) matrix[0][j] = j;

            for (let i = 1; i <= b.length; i++) {
              for (let j = 1; j <= a.length; j++) {
                if (b.charAt(i - 1) === a.charAt(j - 1)) {
                  matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                  matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1,
                    matrix[i][j - 1] + 1,
                    matrix[i - 1][j] + 1
                  );
                }
              }
            }

            return matrix[b.length][a.length];
          };

          return (longer.length - editDistance(longer, shorter)) / longer.length;
        };

        const isMatch = (alt, color) => {
          const a = normalize(alt);
          const c = normalize(color);

          if (!a) return false;
          if (a === c) return true;
          if (a.includes(c) || c.includes(a)) return true;

          const aWords = a.split(' ');
          const cWords = c.split(' ');
          if (cWords.some((word) => aWords.includes(word))) return true;
          if (similarity(a, c) >= 0.85) return true;

          return false;
        };

        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery) return;

        const items = Array.from(
          mediaGallery.querySelectorAll('li[data-media-id]')
        );

        let matchCount = 0;
        let firstMatch = null;

        items.forEach((item) => {
          const img = item.querySelector('img');
          if (!img) return;

          const alt = img.getAttribute('alt');

          if (isMatch(alt, selectedColor)) {
            item.style.display = '';
            matchCount++;
            if (!firstMatch) firstMatch = item;
          } else {
            item.style.display = 'none';
          }
        });

        if (matchCount === 0) {
          items.forEach((item) => (item.style.display = ''));
          return;
        }

        if (firstMatch) {
          const mediaId = firstMatch.dataset.mediaId.split('-').pop();
          mediaGallery.setActiveMedia?.(
            `${this.dataset.section}-${mediaId}`,
            true
          );
        }
      }

      // =========================
      // ✅ QUANTITY
      // =========================
      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityInput.min = 1;
        this.quantityInput.value = 1;
      }

      get productForm() {
        return this.querySelector('product-form');
      }
    }
  );
}